/**
 * Tiện ích cuộn (scroll) chung của dự án.
 *
 * Lý do tồn tại:
 *  - `Element.scrollIntoView()` của trình duyệt sẽ cuộn TẤT CẢ ancestor cuộn
 *    được — bao gồm cả `window`/`document` và các container `overflow-hidden`
 *    bao ngoài (chúng vẫn có thể bị set `scrollTop` programmatically).
 *  - Trong các layout có nhiều lớp lồng nhau (header cố định, container
 *    `overflow-hidden`, vùng nội dung chính cuộn riêng…), hành vi đó có thể
 *    kéo cả trang lên làm mất phần header/breadcrumb ở trên cùng.
 *
 * Giải pháp: chỉ tìm đúng container cuộn dọc gần nhất (có `overflow-y` là
 * `auto` hoặc `scroll`) rồi gọi `scrollTo` trên container đó. Các ancestor
 * khác không bị đụng tới.
 */

/**
 * Tìm phần tử ancestor cuộn dọc gần nhất của `el`.
 *
 * Bỏ qua các container `overflow-hidden` / `visible` (không cuộn được bằng UI),
 * cũng như các container có nội dung không vượt quá chiều cao (không có gì
 * để cuộn). Trả về `null` nếu không tìm thấy — khi đó nên fallback về
 * `Element.scrollIntoView()` của trình duyệt.
 */
export const getScrollableParent = (
    el: HTMLElement | null | undefined
): HTMLElement | null => {
    let node = el?.parentElement ?? null;
    while (node) {
        const { overflowY } = getComputedStyle(node);
        const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
        if (isScrollable && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

/**Tùy chọn cho `scrollElementIntoView`. */
export type ScrollElementIntoViewOptions = {
    /**
     * Vị trí mong muốn của target sau khi cuộn, tương ứng `ScrollIntoViewOptions.block`:
     *  - `start`   : đưa target lên đầu vùng cuộn (mặc định).
     *  - `center`  : căn target vào giữa vùng cuộn.
     *  - `nearest` : chỉ cuộn nếu target đang nằm ngoài vùng nhìn (cuộn ngắn nhất).
     */
    block?: 'start' | 'center' | 'nearest';
    /**`'smooth'` (mặc định) hoặc `'auto'`. */
    behavior?: ScrollBehavior;
    /**
     * Khoảng đệm (px) trừ thêm từ vị trí đích — ví dụ muốn chừa khoảng trống
     * phía trên target cho thanh tiêu đề sticky. Mặc định 0.
     */
    offset?: number;
};

/**
 * Cuộn `target` vào vùng nhìn — chỉ tác động lên container cuộn gần nhất,
 * KHÔNG kéo theo các ancestor `overflow-hidden`/window.
 *
 * Nếu `target` đã nằm trong vùng nhìn của container và `block === 'nearest'`,
 * sẽ không cuộn gì cả. Nếu không tìm được container cuộn dọc nào, sẽ fallback
 * về `Element.scrollIntoView()` của trình duyệt.
 */
export const scrollElementIntoView = (
    target: HTMLElement | null | undefined,
    options: ScrollElementIntoViewOptions = {}
) => {
    if (!target) return;

    const { block = 'start', behavior = 'smooth', offset = 0 } = options;

    const scroller = getScrollableParent(target);
    if (!scroller) {
        // Không có vùng cuộn riêng → buộc phải dùng API native.
        target.scrollIntoView({ behavior, block });
        return;
    }

    const targetRect = target.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();

    // Toạ độ y của target trong hệ trục nội dung của scroller.
    const targetTopInScroller =
        targetRect.top - scrollerRect.top + scroller.scrollTop;
    const targetBottomInScroller = targetTopInScroller + targetRect.height;

    let top: number;

    if (block === 'center') {
        top =
            targetTopInScroller -
            scroller.clientHeight / 2 +
            targetRect.height / 2 -
            offset;
    } else if (block === 'nearest') {
        const visibleTop = scroller.scrollTop;
        const visibleBottom = visibleTop + scroller.clientHeight;
        const isFullyVisible =
            targetTopInScroller >= visibleTop &&
            targetBottomInScroller <= visibleBottom;

        if (isFullyVisible) return; // Không cần cuộn

        // Cuộn ngắn nhất: nếu phía trên không thấy → đưa lên đầu;
        // ngược lại đưa xuống đáy vừa đủ thấy.
        top =
            targetTopInScroller < visibleTop
                ? targetTopInScroller - offset
                : targetBottomInScroller - scroller.clientHeight + offset;
    } else {
        // 'start'
        top = targetTopInScroller - offset;
    }

    // Giới hạn trong khoảng cuộn hợp lệ [0, scrollHeight - clientHeight].
    const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    if (top < 0) top = 0;
    if (top > max) top = max;

    scroller.scrollTo({ top, behavior });
};
